import React, { useContext, useState, useEffect } from "react";
import ViewCartLink from "./ViewCartLink";
import { EventGalleryContext } from "./index";
import ArrowDown from "./vectors/ArrowDown";
import { PrintProduct } from "../CheckoutPage/components/types";
import AddToCartButton from "./AddToCartButton";
import RemoveFromCartButton from "./RemoveFromCartButton";

interface PrintLightboxHeaderProps {
  photo: any;
  primaryColor?: string;
  addToCart: (photo: any) => void;
  removeFromCart?: (photo: any) => void;
}

const PrintLightboxHeader: React.FC<PrintLightboxHeaderProps> = ({
  photo,
  addToCart,
  removeFromCart,
  primaryColor = "#259fa5"
}) => {
  const context = useContext(EventGalleryContext);
  const [printProducts, setPrintProducts] = useState<PrintProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [showProducts, setShowProducts] = useState(false);

  const handleSelectProducts = () => {
    // Scroll to the print products section
    const printProductsSection = document.querySelector(
      ".mobile-print-products-section"
    );
    if (printProductsSection) {
      printProductsSection.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };

  const handleViewPriceList = async () => {
    setShowProducts(true);
    if (printProducts.length === 0 && photo?.id) {
      await fetchPrintProducts();
    }
  };

  const fetchPrintProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/v1/photos/${photo?.id}/print_products`
      );
      if (response.ok) {
        const data = await response.json();
        setPrintProducts(data.printProducts || data || []);
      }
    } catch (error) {
      console.error("Error fetching print products:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number | string, currency: string) => {
    const numericPrice = typeof price === "string" ? parseFloat(price) : price;
    if (isNaN(numericPrice) || numericPrice == null) {
      return `$0.00 ${currency}`;
    }
    return `${numericPrice.toFixed(2)} ${currency}`;
  };

  const handleHidePriceList = () => {
    setShowProducts(false);
  };

  const hasCartItems = () => {
    return context.cart?.photos?.includes(photo.id) || false;
  };

  // Legacy view (when flipper flag is disabled)
  if (!context.lightboxPrintProductsEnabled) {
    return (
      <div className="geo-legacy-print-footer">
        <div className="geo-legacy-print-footer-text">
          {/* Action row - left aligned with add/remove button first, then view/hide button */}
          <div
            className={`geo-legacy-print-footer__actions d-flex align-items-center ${
              showProducts ? "has-products" : ""
            }`}
            style={{ justifyContent: "flex-start", gap: "12px" }}
          >
            {/* Add/Remove Cart Button */}
            {photo?.isSelected ? (
              <RemoveFromCartButton
                colorTheme={primaryColor}
                onRemoveFromCart={() => {
                  removeFromCart?.(photo);
                }}
                photo={photo}
              />
            ) : (
              <AddToCartButton
                colorTheme={primaryColor}
                onAddToCart={() => {
                  addToCart?.(photo);
                }}
                photo={photo}
              />
            )}

            {/* View/Hide Price List Button */}
            <button
              onClick={showProducts ? handleHidePriceList : handleViewPriceList}
              className="geo-legacy-text-btn"
              style={{ color: primaryColor }}
            >
              {showProducts ? "Hide price list" : "View price list"}
            </button>
          </div>

          {/* Header text - moved below the buttons */}
          <div
            className="geo-legacy-print-footer__header"
            style={{ marginTop: "6px", marginBottom: 0 }}
          >
            <div className="geo-legacy-print-footer__text">
              You can <strong>customise products</strong> in Cart once you add
              this photo to cart
            </div>
          </div>
        </div>

        {/* Product list - only show when showProducts is true */}
        {showProducts && (
          <div className="geo-legacy-print-footer__products">
            {loading ? (
              <div className="text-center geo-small-section-loader">
                <i className="fa fa-spinner fa-spin"></i>
                <span>Loading products...</span>
              </div>
            ) : (
              <div
                className={`geo-legacy-print-footer__product-grid ${
                  printProducts.length > 2 ? "two-columns" : ""
                }`}
              >
                {printProducts.length > 2 ? (
                  <div className="d-flex">
                    {/* Left column */}
                    <div className="geo-legacy-print-footer__product-column">
                      {printProducts
                        .slice(0, Math.ceil(printProducts.length / 2))
                        .map(product => (
                          <div
                            key={product.id}
                            className="geo-legacy-print-footer__product-row d-flex align-items-center"
                          >
                            <div className="geo-legacy-print-footer__product-name">
                              {product.title}
                            </div>
                            <div className="geo-legacy-print-footer__product-dots"></div>
                            <div className="geo-legacy-print-footer__product-price">
                              {formatPrice(
                                product.totalPrice,
                                product.currency
                              )}
                            </div>
                          </div>
                        ))}
                    </div>

                    {/* Column divider */}
                    <div className="geo-legacy-print-footer__column-divider"></div>

                    {/* Right column */}
                    <div className="geo-legacy-print-footer__product-column">
                      {printProducts
                        .slice(Math.ceil(printProducts.length / 2))
                        .map(product => (
                          <div
                            key={product.id}
                            className="geo-legacy-print-footer__product-row d-flex align-items-center"
                          >
                            <div className="geo-legacy-print-footer__product-name">
                              {product.title}
                            </div>
                            <div className="geo-legacy-print-footer__product-dots"></div>
                            <div className="geo-legacy-print-footer__product-price">
                              {formatPrice(
                                product.totalPrice,
                                product.currency
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ) : (
                  /* Single column for 2 or fewer items */
                  printProducts.map(product => (
                    <div
                      key={product.id}
                      className="geo-legacy-print-footer__product-row d-flex align-items-center"
                    >
                      <div className="geo-legacy-print-footer__product-name">
                        {product.title}
                      </div>
                      <div className="geo-legacy-print-footer__product-dots"></div>
                      <div className="geo-legacy-print-footer__product-price">
                        {formatPrice(product.totalPrice, product.currency)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // New design (when flipper flag is enabled)
  return (
    <div className="geo-print-header-mobile">
      {/* Action buttons at top */}
      <div className="geo-print-header-mobile__actions">
        <button
          onClick={handleSelectProducts}
          className="scroll-lightbox-btn"
          style={{
            color: `${primaryColor}`
          }}
        >
          Select products to add to cart <ArrowDown color={primaryColor} />
        </button>

        {hasCartItems() && context.cart && (
          <ViewCartLink
            parentName="mobile-print-lightbox"
            colorTheme={primaryColor}
            cartId={context.cart.id}
            isEmbed={context.isEmbed || false}
          />
        )}
      </div>

      {/* Explanatory text below buttons */}
      <div className="geo-print-header-mobile__text"></div>
    </div>
  );
};

export default PrintLightboxHeader;
